import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader } from "../ui";
import { blankForm } from "./admin-recibos/shared";
import { ReceiptCreateForm } from "./admin-recibos/ReceiptCreateForm";
import { ReceiptFilters } from "./admin-recibos/ReceiptFilters";
import { ReceiptList } from "./admin-recibos/ReceiptList";
import { ReceiptCancelModal } from "./admin-recibos/ReceiptCancelModal";


export default function AdminRecibos() {
  const [items, setItems] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [associados, setAssociados] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [filter, setFilter] = useState("todos");

  const load = () =>
    api
      .get("/admin/receipts")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar recibos."));

  useEffect(() => {
    load();
    api
      .get("/admin/associados")
      .then((r) =>
        setAssociados(
          (r.data || []).filter((a) => a.status === "ativo"),
        ),
      )
      .catch(() => {});
  }, []);

  const onAssociateChange = (id) => {
    if (!id) {
      setForm((f) => ({ ...f, associate_id: "" }));
      return;
    }
    const a = associados.find((x) => x.id === id);
    if (a) {
      setForm((f) => ({
        ...f,
        associate_id: id,
        payer_person_type: a.person_type === "PJ" ? "PJ" : "PF",
        payer_name: a.name || "",
        payer_document: a.cpf_cnpj || "",
        payer_address: a.address || "",
        payer_email: a.boleto_email || a.email || "",
      }));
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    const amt = parseFloat(String(form.amount).replace(",", "."));
    if (!amt || amt <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount: amt,
        associate_id: form.associate_id || null,
        payer_email: form.payer_email || null,
        boleto_number: form.boleto_number?.trim() || null,
      };
      const r = await api.post("/admin/receipts", payload);
      toast.success(`Recibo ${r.data.number} emitido!`);
      setOpenCreate(false);
      setForm(blankForm());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async (r) => {
    try {
      const resp = await api.get(`/admin/receipts/${r.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Recibo-${r.number.replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Falha ao gerar PDF.");
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    if (cancelReason.trim().length < 3) {
      toast.error("Informe o motivo do cancelamento.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/receipts/${cancelling.id}/cancel`, {
        reason: cancelReason.trim(),
      });
      toast.success("Recibo cancelado. Notificação enviada.");
      setCancelling(null);
      setCancelReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const filtered = (items || []).filter((r) => {
    if (filter === "todos") return true;
    return r.status === filter;
  });

  return (
    <div data-testid="admin-recibos">
      <PageHeader
        title="Recibos"
        subtitle="Emita recibos numerados para associados e não associados. Os recibos não podem ser editados, apenas cancelados. Quando cancelados, o destinatário é notificado por e-mail automaticamente."
        actions={
          <button
            data-testid="new-receipt-btn"
            onClick={() => {
              setOpenCreate((v) => !v);
              setForm(blankForm());
            }}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Emitir recibo
          </button>
        }
      />

      <ReceiptCreateForm associados={associados} busy={busy} form={form} items={items} onAssociateChange={onAssociateChange} openCreate={openCreate} setForm={setForm} setOpenCreate={setOpenCreate} submitCreate={submitCreate} />
      <ReceiptFilters filter={filter} filtered={filtered} items={items} setFilter={setFilter} />
      <ReceiptList downloadPdf={downloadPdf} filtered={filtered} items={items} setCancelling={setCancelling} />
      <ReceiptCancelModal busy={busy} cancelReason={cancelReason} cancelling={cancelling} confirmCancel={confirmCancel} items={items} setCancelReason={setCancelReason} setCancelling={setCancelling} />
    </div>
  );
}

